import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  isEmailConfirmationLocked,
  sendEmailConfirmation,
  EMAIL_CONFIRMATION_RESEND_COOLDOWN_MS,
} from '../../lib/emailConfirmation';
import { Button, Card } from '../../components/ui/Kit';

/**
 * Hard wall for users past the email-confirmation grace period.
 * Session stays valid — they just cannot enter the app until confirmed.
 */
export default function EmailConfirmationRequired() {
  const navigate = useNavigate();
  const { user, loading, logout, refreshUser } = useAuth();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownLeft, setCooldownLeft] = useState(0);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isEmailConfirmationLocked(user)) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!cooldownUntil) {
      setCooldownLeft(0);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setCooldownLeft(left);
      if (left <= 0) setCooldownUntil(0);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const handleResend = async () => {
    if (resending || cooldownLeft > 0) return;
    setError('');
    setMessage('');
    setResending(true);
    try {
      const result = await sendEmailConfirmation();
      if (result?.already_confirmed) {
        await refreshUser();
        navigate('/dashboard', { replace: true });
        return;
      }
      if (result?.cooldown) {
        setMessage(result.message || 'Please wait a moment before requesting another confirmation email.');
      } else {
        setMessage('Check your inbox for a confirmation link.');
      }
      setCooldownUntil(Date.now() + EMAIL_CONFIRMATION_RESEND_COOLDOWN_MS);
    } catch (err) {
      setError(err.message || 'Could not send confirmation email.');
    } finally {
      setResending(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  if (loading || !user || !isEmailConfirmationLocked(user)) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full p-8 shadow-xl border-0 ring-1 ring-gray-200 text-center">
        <div className="mx-auto h-12 w-12 bg-primary rounded-lg shadow-sm flex items-center justify-center mb-4">
          <span className="text-white font-bold text-2xl">P</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Please confirm your email to continue</h2>
        <p className="text-sm text-gray-600 mb-6">
          Your account is signed in, but email confirmation is required before you can use the CRM.
          Check your inbox for a confirmation link, or resend one below.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md border border-red-100 text-left">
            {error}
          </div>
        )}
        {message && (
          <div className="mb-4 p-3 bg-green-50 text-green-800 text-sm rounded-md border border-green-100 text-left">
            {message}
          </div>
        )}

        <div className="space-y-3">
          <Button
            type="button"
            className="w-full"
            isLoading={resending}
            disabled={cooldownLeft > 0}
            onClick={() => void handleResend()}
            size="lg"
          >
            {cooldownLeft > 0
              ? `Resend confirmation email (${cooldownLeft}s)`
              : 'Resend confirmation email'}
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={() => void handleSignOut()}>
            Sign out
          </Button>
        </div>
      </Card>
    </div>
  );
}
